<template>
  <div class="brandy-agent" :class="stateClasses">
    <!-- Chat Trigger (Floating icon / Popup) -->
    <transition name="brandy-fade">
      <div
        v-if="uiState === 'minimized'"
        class="brandy-agent__minimized"
        :class="{ 'brandy-agent__minimized--popup': isExpanded }">
        <!-- Welcome Popup -->
        <transition name="brandy-popup">
          <div v-if="isExpanded" class="brandy-agent__popup">
            <div class="brandy-agent__popup-header" @click="openChat">
              <img
                class="brandy-agent__popup-avatar"
                alt="Avatar"
                :src="customLogo || trioLogoSvg" />
              <div class="brandy-agent__popup-info">
                <span class="brandy-agent__popup-title">{{
                  s.headerTitle
                }}</span>
                <span class="brandy-agent__popup-subtitle">{{
                  s.headerSubtitle
                }}</span>
              </div>
              <button
                class="brandy-agent__popup-close"
                @click.stop="isExpanded = false"
                aria-label="Close">
                <i class="fal fa-times"></i>
              </button>
            </div>
            <div class="brandy-agent__popup-body" @click="openChat">
              <!-- First popup message -->
              <div v-if="showPopupFirstMessage" class="brandy-agent__popup-msg">
                <img
                  class="brandy-agent__popup-msg-avatar"
                  :alt="s.name"
                  :src="customLogo || trioLogoSvg" />
                <div class="brandy-agent__popup-msg-content">
                  <p>{{ s.greeting }}</p>
                </div>
              </div>

              <!-- Typing indicator for second popup message -->
              <div
                v-if="isPopupTyping"
                class="brandy-agent__popup-msg"
                :style="showPopupFirstMessage ? 'margin-top: 15px;' : ''">
                <img
                  class="brandy-agent__popup-msg-avatar"
                  :alt="s.name"
                  :src="customLogo || trioLogoSvg" />
                <div
                  class="brandy-agent__popup-msg-content"
                  style="padding-top: 5px">
                  <span class="brandy-agent__typing">
                    <span></span><span></span><span></span>
                  </span>
                </div>
              </div>

              <!-- Second popup message -->
              <div
                v-if="showPopupSecondMessage"
                class="brandy-agent__popup-msg"
                style="margin-top: 15px">
                <img
                  class="brandy-agent__popup-msg-avatar"
                  :alt="s.name"
                  :src="customLogo || trioLogoSvg" />
                <div class="brandy-agent__popup-msg-content">
                  <p>{{ s.popupGreeting }}</p>
                </div>
              </div>

              <!-- Playful message -->
              <div
                v-if="showPopupPlayfulMessage"
                class="brandy-agent__popup-msg">
                <img
                  class="brandy-agent__popup-msg-avatar"
                  :alt="s.name"
                  :src="customLogo || trioLogoSvg" />
                <div class="brandy-agent__popup-msg-content">
                  <p>{{ s.playfulGreeting }}</p>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <!-- Avatar Icon -->
        <button
          v-if="!isExpanded"
          type="button"
          class="brandy-agent__trigger"
          @click="openChat"
          :aria-label="s.popupCta">
          <img
            class="brandy-agent__trigger-logo"
            fetchpriority="high"
            alt="Logo"
            :src="customLogo || trioLogoSvg" />
          <span class="brandy-agent__trigger-badge">1</span>
        </button>
      </div>
    </transition>

    <!-- Chat Overlay -->
    <transition name="brandy-overlay">
      <div
        v-if="uiState === 'open'"
        class="brandy-agent__overlay"
        @click.self="minimizeChat">
        <div class="brandy-agent__container">
          <!-- Header -->
          <header class="brandy-agent__header">
            <img
              class="brandy-agent__header-logo"
              alt="Logo"
              :src="customLogo || trioLogoSvg" />
            <div class="brandy-agent__header-info">
              <span class="brandy-agent__header-title">{{
                s.headerTitle
              }}</span>
              <span class="brandy-agent__header-subtitle">{{
                s.headerSubtitle
              }}</span>
            </div>
            <button
              class="brandy-agent__header-collapse"
              @click="minimizeChat"
              aria-label="Minimize">
              <i
                class="fal brandy-agent__header-collapse-icon"
                :class="{
                  'fa-close': !isMobile(),
                  'fa-angle-down': isMobile(),
                }"></i>
            </button>
          </header>

          <!-- Messages area -->
          <!-- Scroll-to-bottom pill — shown when new content arrives while the user is scrolled up -->
          <transition name="brandy-scroll-btn">
            <button
              v-if="hasNewMessagesWhileScrolledUp"
              class="brandy-agent__scroll-btn"
              type="button"
              @click="scrollToBottomManually"
              aria-label="Scroll to latest message">
              <i class="fa fa-angle-down" aria-hidden="true"></i>
              <span>{{ s.newMessage || 'Neue Nachricht' }}</span>
            </button>
          </transition>

          <main
            class="brandy-agent__messages"
            ref="messagesContainer"
            @scroll.passive="onMessagesScroll">
            <div v-for="msg in displayMessages" :key="msg.id">
              <!-- Bot message -->
              <div
                v-if="msg.role === 'assistant'"
                class="brandy-agent__msg brandy-agent__msg--bot"
                :class="{ 'brandy-agent__msg--streaming': msg.isStreaming }">
                <img
                  v-if="customLogo"
                  class="brandy-agent__msg-avatar"
                  :src="customLogo"
                  alt="Agent" />
                <span v-else class="brandy-agent__msg-dot"></span>
                <div class="brandy-agent__msg-content">
                  <div
                    class="brandy-agent__msg-inner paragraph-text"
                    v-html="msg.content || ''"></div>
                  <span
                    v-if="msg.isStreaming"
                    class="brandy-agent__typing brandy-agent__typing--inline"
                    aria-label="Brandy ist noch dabei zu schreiben">
                    <span></span><span></span><span></span>
                  </span>
                  <button
                    v-if="msg.isError && lastFailedUserMessage"
                    class="button button--secondary brandy-agent__retry-btn"
                    type="button"
                    @click="retryLastMessage">
                    <i class="fal fa-rotate-right" aria-hidden="true"></i>
                    {{ s.retryButton }}
                  </button>
                  <!-- PDF download (inline with message) -->
                  <div v-if="msg.pdfUrl" class="brandy-agent__pdf">
                    <a
                      :href="msg.pdfUrl"
                      target="_blank"
                      download
                      class="brandy-agent__pdf-link">
                      <img
                        class="brandy-agent__pdf-icon"
                        :src="pdfIconSvg"
                        alt="PDF" />
                      <span class="brandy-agent__pdf-name">{{
                        msg.pdfFilename
                      }}</span>
                      <!-- eslint-disable vue/first-attribute-linebreak -->
                      <i
                        class="fa-light fa-arrow-down-to-line brandy-agent__pdf-download">
                      </i>
                      <!-- eslint-enable vue/first-attribute-linebreak -->
                    </a>
                  </div>
                </div>
              </div>

              <!-- User message -->
              <div
                v-else-if="msg.role === 'user'"
                class="brandy-agent__msg brandy-agent__msg--user">
                <div class="brandy-agent__msg-content">
                  {{ msg.text || msg.content || '' }}
                </div>
              </div>
            </div>

            <!-- Typing indicator -->
            <div
              v-if="isLoading"
              class="brandy-agent__msg brandy-agent__msg--bot brandy-agent__msg--typing">
              <img
                v-if="customLogo"
                class="brandy-agent__msg-avatar"
                :src="customLogo"
                alt="Agent" />
              <span v-else class="brandy-agent__msg-dot"></span>
              <div class="brandy-agent__msg-content">
                <span class="brandy-agent__typing">
                  <span></span><span></span><span></span>
                </span>
              </div>
            </div>

            <!-- Suggestion buttons -->
            <div
              v-if="currentSuggestions.length && !isLoading"
              class="brandy-agent__suggestions">
              <button
                v-for="(suggestion, sIdx) in currentSuggestions"
                :key="sIdx"
                class="brandy-agent__suggestion"
                @click="handleSuggestion(suggestion)">
                <span>{{ suggestion.label }}</span>
                <i class="fa fa-send" aria-hidden="true"></i>
              </button>
            </div>
          </main>

          <!-- Input area -->
          <footer class="brandy-agent__input">
            <textarea
              id="brandy-chat-input"
              ref="chatInput"
              v-model="userInput"
              rows="1"
              class="brandy-agent__input-text"
              :placeholder="inputPlaceholder"
              :disabled="isLoading || isCoolingDown || !hasInteracted"
              @keydown.enter.prevent="sendUserMessage">
            </textarea>
            <button
              class="brandy-agent__send"
              :class="{
                'brandy-agent__send--active': hasInteracted && !isCoolingDown,
              }"
              :disabled="isLoading || isCoolingDown || !hasInteracted"
              @click="sendUserMessage"
              :aria-label="isCoolingDown ? cooldownAriaLabel : 'Send'">
              <i class="fa fa-send" aria-hidden="true"></i>
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import translationStrings from '../js/utils/translationStrings';
import trioLogoSvg from '../images/logo-trio-group-short.svg';
import pdfIconSvg from '../images/pdf-icon.svg';
import isMobileUtil from '../js/utils/isMobile';
import {
  detectPdfUrl,
  extractMessageText,
  renderAssistantContent,
  stripHtml,
} from '../js/utils/brandyMarkdown';
import {
  parseRetryAfterSeconds,
  readBrandyStream,
} from '../js/utils/brandyStream';

const HISTORY_LIMIT = 10;
const SCROLL_THRESHOLD = 80;
const MIN_SEND_GAP_MS = 1500;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function createUuid() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random % 4) + 8;
    return value.toString(16);
  });
}

// Storage keys are per agent so Brandy and Bonnie never share a chat log.
function getSessionChatId(agent) {
  const key = `${agent}_chat_id`;
  try {
    const stored = sessionStorage.getItem(key);
    if (stored && UUID_PATTERN.test(stored)) return stored;

    const id = createUuid();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return createUuid();
  }
}

function hasStoredBubbleInitiated(agent) {
  try {
    return sessionStorage.getItem(`${agent}_bubble_initiated`) === 'true';
  } catch {
    return false;
  }
}

function setStoredBubbleInitiated(agent) {
  try {
    sessionStorage.setItem(`${agent}_bubble_initiated`, 'true');
  } catch {
    // Private browsing or blocked storage should not break the chat.
  }
}

export default {
  name: 'BrandyAgent',

  props: {
    agent: {
      type: String,
      default: 'brandy',
    },
    customLogo: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      t: translationStrings,
      trioLogoSvg,
      pdfIconSvg,
      uiState: 'minimized',
      hasInteracted: false,
      isExpanded: false,
      showPopupFirstMessage: false,
      showPopupSecondMessage: false,
      showPopupPlayfulMessage: false,
      isPopupTyping: false,
      messages: [],
      userInput: '',
      isLoading: false,
      msgIdCounter: 0,
      playfulTimer: null,
      playfulSequenceStarted: false,
      popupTimers: [],
      lastFailedUserMessage: null,
      chatId: getSessionChatId(this.agent),
      hasNewMessagesWhileScrolledUp: false,
      cooldownUntil: 0,
      cooldownTimer: null,
      now: Date.now(),
      lastSendAt: 0,
      csrfToken: '',
      csrfTokenPromise: null,
      activeStreamBotMsgIndex: -1,
      activeStreamSnapshot: null,
    };
  },

  computed: {
    // Persona strings: Brandy's as base, overridden by the active agent.
    s() {
      return { ...this.t.brandy, ...this.t[this.agent] };
    },

    stateClasses() {
      return {
        'brandy-agent--minimized': this.uiState === 'minimized',
        'brandy-agent--open': this.uiState === 'open',
        'brandy-agent--locked': !this.hasInteracted,
      };
    },

    displayMessages() {
      return this.messages;
    },

    currentSuggestions() {
      if (this.messages.length === 0) return [];

      const lastBotMsg = this.messages
        .slice()
        .reverse()
        .find((message) => message.role === 'assistant');
      if (!lastBotMsg) return [];

      if (!this.hasInteracted) {
        return [
          {
            label: this.s.suggestionStartAnalysis,
            text: this.s.suggestionStartAnalysis,
          },
          {
            label: this.s.suggestionBenefits,
            text: this.s.suggestionBenefits,
          },
          {
            label: this.s.suggestionBackground,
            text: this.s.suggestionBackground,
          },
        ];
      }

      const hasUserSentAnalysisRequest = this.messages.some(
        (message) =>
          message.role === 'user' &&
          message.text === this.s.suggestionStartAnalysis,
      );
      const hasUserSentBrandMap = this.messages.some(
        (message) =>
          message.role === 'user' &&
          message.text === this.s.suggestionGenerateBrandMap,
      );

      if (
        this.s.suggestionGenerateBrandMap &&
        hasUserSentAnalysisRequest &&
        !hasUserSentBrandMap
      ) {
        const content = (lastBotMsg.content || '').toLowerCase();
        if (
          content.includes('brand map') ||
          content.includes('brandmap') ||
          content.includes('generieren')
        ) {
          return [
            {
              label: this.s.suggestionGenerateBrandMap,
              text: this.s.suggestionGenerateBrandMap,
            },
          ];
        }
      }

      return [];
    },

    cooldownSecondsRemaining() {
      const ms = this.cooldownUntil - this.now;
      return ms > 0 ? Math.ceil(ms / 1000) : 0;
    },

    isCoolingDown() {
      return this.cooldownSecondsRemaining > 0;
    },

    inputPlaceholder() {
      if (!this.isCoolingDown) return this.s.inputPlaceholder;
      const template =
        this.s.cooldownPlaceholder || 'Bitte @seconds s warten...';
      return template.replace(
        '@seconds',
        String(this.cooldownSecondsRemaining),
      );
    },

    cooldownAriaLabel() {
      const template =
        this.s.cooldownPlaceholder || 'Bitte @seconds s warten...';
      return template.replace(
        '@seconds',
        String(this.cooldownSecondsRemaining),
      );
    },
  },

  watch: {
    isExpanded(newValue) {
      if (
        !newValue &&
        this.uiState === 'minimized' &&
        !this.hasInteracted &&
        !this.playfulSequenceStarted
      ) {
        this.startPlayfulSequence();
      }
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    this.startWelcomePopup();

    this.setTrackedTimeout(() => {
      if (this.isExpanded && this.uiState === 'minimized') {
        this.isExpanded = false;
      }
    }, 10000);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.clearTrackedTimers();

    if (this.cooldownTimer !== null) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    if (this.playfulTimer !== null) {
      clearTimeout(this.playfulTimer);
      this.playfulTimer = null;
    }
  },

  methods: {
    isMobile() {
      return isMobileUtil();
    },

    setTrackedTimeout(callback, delay) {
      const timer = setTimeout(callback, delay);
      this.popupTimers.push(timer);
      return timer;
    },

    clearTrackedTimers() {
      this.popupTimers.forEach((timer) => clearTimeout(timer));
      this.popupTimers = [];
    },

    generateMsgId() {
      this.msgIdCounter += 1;
      return `msg_${Date.now()}_${this.msgIdCounter}`;
    },

    beginCooldown(seconds) {
      const ms = Math.max(1, Math.min(600, Math.ceil(seconds))) * 1000;
      const target = Date.now() + ms;
      if (target <= this.cooldownUntil) return;

      this.cooldownUntil = target;
      if (this.cooldownTimer === null) {
        this.cooldownTimer = setInterval(() => {
          this.now = Date.now();
          if (this.now >= this.cooldownUntil) {
            clearInterval(this.cooldownTimer);
            this.cooldownTimer = null;
          }
        }, 250);
      }
    },

    openChat() {
      if (this.playfulTimer) clearTimeout(this.playfulTimer);
      this.playfulSequenceStarted = true;
      this.uiState = 'open';
      this.isExpanded = false;

      if (this.messages.length === 0) {
        this.isLoading = true;

        this.setTrackedTimeout(() => {
          this.messages.push({
            id: this.generateMsgId(),
            role: 'assistant',
            content: `${this.s.greeting} ${this.s.chatGreeting}`,
          });
          this.isLoading = false;
          this.scrollToBottom();
        }, 1500);
      }

      this.$nextTick(() => {
        this.scrollToBottom(true);
        this.$refs.chatInput?.focus();
      });
    },

    minimizeChat() {
      this.uiState = 'minimized';
    },

    handleSuggestion(suggestion) {
      this.hasInteracted = true;
      this.isExpanded = false;
      this.sendText(suggestion.text);
    },

    sendUserMessage() {
      const text = this.userInput.trim();
      if (!text || this.isLoading || this.isCoolingDown) return;

      const elapsed = Date.now() - this.lastSendAt;
      if (elapsed < MIN_SEND_GAP_MS) {
        this.beginCooldown((MIN_SEND_GAP_MS - elapsed) / 1000);
        return;
      }

      this.lastSendAt = Date.now();
      this.hasInteracted = true;
      this.isExpanded = false;
      this.userInput = '';
      this.sendText(text);
    },

    retryLastMessage() {
      const text = this.lastFailedUserMessage;
      if (!text || this.isLoading) return;

      this.messages = this.messages.filter((message) => !message.isError);
      this.lastFailedUserMessage = null;
      this.lastSendAt = 0;
      this.sendText(text);
    },

    sendText(text) {
      this.lastFailedUserMessage = null;
      this.messages.push({
        id: this.generateMsgId(),
        role: 'user',
        text,
        content: text,
      });

      this.scrollToBottom(true);
      this.callAgent();
    },

    buildApiMessages() {
      return this.messages
        .filter((message) => !message.isError)
        .slice(-HISTORY_LIMIT)
        .map((message) => {
          const text =
            message.role === 'user'
              ? message.text || message.content || ''
              : stripHtml(message.content || '');

          return {
            id: message.id,
            role: message.role,
            parts: [{ type: 'text', text }],
          };
        })
        .filter((message) => message.parts[0].text.trim() !== '');
    },

    async getCsrfToken() {
      if (this.csrfToken) return this.csrfToken;

      if (!this.csrfTokenPromise) {
        this.csrfTokenPromise = fetch('/session/token', {
          credentials: 'same-origin',
        })
          .then((response) => (response.ok ? response.text() : ''))
          .then((token) => {
            this.csrfToken = token.trim();
            this.csrfTokenPromise = null;
            return this.csrfToken;
          })
          .catch(() => {
            this.csrfTokenPromise = null;
            return '';
          });
      }

      return this.csrfTokenPromise;
    },

    async callAgent() {
      this.isLoading = true;
      this.activeStreamBotMsgIndex = -1;
      this.activeStreamSnapshot = null;
      this.scrollToBottom(true);

      try {
        const headers = { 'Content-Type': 'application/json' };
        const csrfToken = await this.getCsrfToken();
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken;
        }

        const response = await fetch('/api/v1/brandy/chat', {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify({
            messages: this.buildApiMessages(),
            stream: true,
            chatId: this.chatId,
            agent: this.agent,
          }),
        });

        if (!response.ok) {
          if (response.status === 429) {
            this.beginCooldown(
              parseRetryAfterSeconds(response.headers.get('Retry-After')),
            );
            const rateLimitError = new Error('rate-limited');
            rateLimitError.code = 'RATE_LIMITED';
            throw rateLimitError;
          }

          const errorText = await this.safeReadErrorMessage(response);
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('text/event-stream')) {
          const result = await this.handleStreamResponse(response);
          if (result.streamError && !this.streamResultHasContent(result)) {
            const streamError = new Error(result.streamError.message);
            streamError.code =
              result.streamError.status === 429 ? 'RATE_LIMITED' : 'STREAM';
            throw streamError;
          }
          return;
        }

        await this.handleJsonResponse(response);
      } catch (error) {
        console.error('Brandy Agent error:', error);
        this.closeActiveStreamMessage();

        if (this.activeStreamHasContent()) {
          this.lastFailedUserMessage = null;
          return;
        }

        const isRateLimited = error && error.code === 'RATE_LIMITED';
        const lastUserMsg = this.messages
          .slice()
          .reverse()
          .find((message) => message.role === 'user');

        this.lastFailedUserMessage =
          lastUserMsg?.text || lastUserMsg?.content || null;
        this.messages.push({
          id: this.generateMsgId(),
          role: 'assistant',
          content: isRateLimited
            ? this.s.errorRateLimited
            : this.s.errorGeneric,
          isError: true,
        });
      } finally {
        this.isLoading = false;
        this.scrollToBottom();
        this.activeStreamBotMsgIndex = -1;
        this.activeStreamSnapshot = null;
      }
    },

    async safeReadErrorMessage(response) {
      try {
        const text = await response.text();
        if (!text) return '';

        try {
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed.error === 'string') return parsed.error;
        } catch {
          // Non-JSON error body.
        }

        return text.slice(0, 200);
      } catch {
        return '';
      }
    },

    async handleJsonResponse(response) {
      const data = await response.json();
      let content = '';

      if (Array.isArray(data.messages) && data.messages.length > 0) {
        content = data.messages
          .map((message) => extractMessageText(message))
          .filter(Boolean)
          .join('\n');
      } else if (data.choices?.[0]?.message?.content) {
        content = data.choices[0].message.content;
      } else if (data.error) {
        content = this.s.errorGeneric;
      }

      if (content) {
        this.messages.push({
          id: this.generateMsgId(),
          role: 'assistant',
          content: renderAssistantContent(content),
        });
        this.detectPdfInResponse(content, this.messages.length - 1);
      }
    },

    async handleStreamResponse(response) {
      let botMsgIndex = -1;
      let renderScheduled = false;

      const ensureBotMessage = () => {
        if (botMsgIndex !== -1) return;

        this.messages.push({
          id: this.generateMsgId(),
          role: 'assistant',
          content: '',
          isStreaming: true,
        });
        botMsgIndex = this.messages.length - 1;
        this.activeStreamBotMsgIndex = botMsgIndex;
        this.isLoading = false;
      };

      const renderSnapshot = (snapshot) => {
        if (!snapshot) return;
        ensureBotMessage();
        this.messages[botMsgIndex].content = renderAssistantContent(
          snapshot.text,
          snapshot.sources,
          snapshot.files,
        );
        this.applyPdfFiles(snapshot.files, botMsgIndex);
        this.scrollToBottom();
      };

      const scheduleRender = (text, sources, files) => {
        ensureBotMessage();
        this.activeStreamSnapshot = { text, sources, files };
        if (renderScheduled) return;

        renderScheduled = true;
        requestAnimationFrame(() => {
          renderScheduled = false;
          renderSnapshot(this.activeStreamSnapshot);
        });
      };

      const result = await readBrandyStream(response, {
        onDelta: scheduleRender,
        onFile: (file) => {
          ensureBotMessage();
          this.applyPdfFile(file, botMsgIndex);
        },
        onError: (streamError) => {
          if (streamError.retryAfter > 0) {
            this.beginCooldown(streamError.retryAfter);
          }
        },
      });

      if (this.streamResultHasContent(result)) {
        renderSnapshot(result);
      }

      if (botMsgIndex !== -1 && this.messages[botMsgIndex]) {
        this.messages[botMsgIndex].isStreaming = false;
      }

      this.detectPdfInResponse(result.text, botMsgIndex);
      return result;
    },

    streamResultHasContent(result) {
      return Boolean(
        result &&
          (result.text || (Array.isArray(result.files) && result.files.length)),
      );
    },

    activeStreamHasContent() {
      if (this.activeStreamSnapshot) {
        return this.streamResultHasContent(this.activeStreamSnapshot);
      }

      const message = this.messages[this.activeStreamBotMsgIndex];
      return Boolean(message && (message.content || message.pdfUrl));
    },

    closeActiveStreamMessage() {
      const index = this.activeStreamBotMsgIndex;
      if (index === -1 || !this.messages[index]) return;

      if (!this.messages[index].content && this.activeStreamSnapshot) {
        this.messages[index].content = renderAssistantContent(
          this.activeStreamSnapshot.text,
          this.activeStreamSnapshot.sources,
          this.activeStreamSnapshot.files,
        );
        this.applyPdfFiles(this.activeStreamSnapshot.files, index);
      }

      this.messages[index].isStreaming = false;
    },

    applyPdfFiles(files, messageIndex) {
      if (!Array.isArray(files)) return;
      files.forEach((file) => this.applyPdfFile(file, messageIndex));
    },

    applyPdfFile(file, messageIndex) {
      if (!file || messageIndex === -1 || !this.messages[messageIndex]) return;

      const mediaType = file.mediaType || '';
      const filename = file.filename || '';
      const isPdf =
        mediaType === 'application/pdf' ||
        filename.toLowerCase().endsWith('.pdf');
      if (!isPdf) return;

      this.messages[messageIndex].pdfUrl = file.url;
      this.messages[messageIndex].pdfFilename = filename || 'brandmap.pdf';
    },

    detectPdfInResponse(content, messageIndex = -1) {
      const pdf = detectPdfUrl(content);
      if (!pdf) return;

      let targetIndex = messageIndex;
      if (targetIndex === -1) {
        targetIndex = this.messages
          .map((message, index) => ({ message, index }))
          .reverse()
          .find((entry) => entry.message.role === 'assistant')?.index;
      }

      if (targetIndex === undefined || !this.messages[targetIndex]) return;

      this.messages[targetIndex].pdfUrl = pdf.url;
      this.messages[targetIndex].pdfFilename = pdf.filename;
    },

    onMessagesScroll() {
      const container = this.$refs.messagesContainer;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
        this.hasNewMessagesWhileScrolledUp = false;
      }
    },

    scrollToBottom(force = false) {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const isAtBottom =
          scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;

        if (force || isAtBottom) {
          container.scrollTop = container.scrollHeight;
          this.hasNewMessagesWhileScrolledUp = false;
        } else {
          this.hasNewMessagesWhileScrolledUp = true;
        }
      });
    },

    scrollToBottomManually() {
      this.hasNewMessagesWhileScrolledUp = false;
      this.$nextTick(() => {
        this.$refs.messagesContainer?.scrollTo({
          top: this.$refs.messagesContainer.scrollHeight,
          behavior: 'smooth',
        });
      });
    },

    handleKeydown(event) {
      if (event.key === 'Escape' && this.uiState === 'open') {
        this.minimizeChat();
      }
    },

    startWelcomePopup() {
      if (hasStoredBubbleInitiated(this.agent)) return;

      setStoredBubbleInitiated(this.agent);
      this.isExpanded = true;

      this.setTrackedTimeout(() => {
        this.isPopupTyping = true;
        this.setTrackedTimeout(this.showWelcomeFirstMessage, 1500);
      }, 500);
    },

    showWelcomeFirstMessage() {
      this.showPopupFirstMessage = true;
      this.isPopupTyping = false;
      this.setTrackedTimeout(this.showWelcomeSecondTyping, 1000);
    },

    showWelcomeSecondTyping() {
      this.isPopupTyping = true;
      this.setTrackedTimeout(this.showWelcomeSecondMessage, 2500);
    },

    showWelcomeSecondMessage() {
      this.isPopupTyping = false;
      this.showPopupSecondMessage = true;
    },

    startPlayfulSequence() {
      if (this.playfulTimer) clearTimeout(this.playfulTimer);
      if (
        this.hasInteracted ||
        this.uiState === 'open' ||
        this.playfulSequenceStarted
      ) {
        return;
      }

      this.playfulSequenceStarted = true;
      this.playfulTimer = setTimeout(() => {
        if (
          this.uiState === 'minimized' &&
          !this.hasInteracted &&
          !this.isExpanded
        ) {
          this.showPopupFirstMessage = false;
          this.showPopupSecondMessage = false;
          this.showPopupPlayfulMessage = true;
          this.isExpanded = true;

          this.playfulTimer = setTimeout(() => {
            if (this.isExpanded && this.uiState === 'minimized') {
              this.isExpanded = false;
            }
          }, 10000);
        }
      }, 10000);
    },
  },
};
</script>
